import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ArrowLeft, Loader2, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import CustomEditor from "../../components/ui/custom-text-editor";
import { api } from "../../utils/api";
import { Announcement } from "../../types";

// Fetch full dashboard data
const getDashboardData = async () => {
  try {
    const response = await api.get("/api/dashboard");
    return response.data || null;
  } catch (error) {
    console.error("Failed to fetch dashboard data:", error);
    return null;
  }
};

// Save announcements
const saveAnnouncements = async (announcements: Announcement[]) => {
  return api.put("/api/dashboard/announcements", { announcements });
};

export default function AnnouncementFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = id && id !== "new";
  const { currentUser } = useAuth();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await getDashboardData();
        if (data) {
          const allAnnouncements: Announcement[] = data.announcements || [];
          setAnnouncements(allAnnouncements);

          if (isEdit) {
            const existing = allAnnouncements.find((a) => a.id === id);
            if (existing) {
              setTitle(existing.title);
              setContent(existing.content || "");
              setImageUrl(existing.imageUrl || "");
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required", {
        description: "Please enter a title for the announcement.",
        icon: <AlertCircle className="h-4 w-4" />,
      });
      return;
    }

    if (!content.trim()) {
      toast.error("Content is required", {
        description: "Please enter some content for the announcement.",
        icon: <AlertCircle className="h-4 w-4" />,
      });
      return;
    }

    setIsSaving(true);
    try {
      let updatedAnnouncements: Announcement[];

      if (isEdit) {
        // Update existing
        updatedAnnouncements = announcements.map((a) =>
          a.id === id
            ? { ...a, title, content, imageUrl: imageUrl || undefined, updatedAt: new Date().toISOString() }
            : a
        );
      } else {
        // Create new
        const newAnnouncement: Announcement = {
          id: crypto.randomUUID(),
          title,
          content,
          imageUrl: imageUrl || undefined,
          createdBy: currentUser?.id,
          createdByName: currentUser?.name,
          createdAt: new Date().toISOString(),
        };
        updatedAnnouncements = [newAnnouncement, ...announcements];
      }

      await saveAnnouncements(updatedAnnouncements);
      navigate("/dashboard");
    } catch (error) {
      console.error("Failed to save announcement:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/dashboard");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={handleCancel}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEdit ? "Edit Announcement" : "New Announcement"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Announcement Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter announcement title"
            />
          </div>

          {/* Image URL */}
          <div className="space-y-2">
            <Label htmlFor="imageUrl">Image URL</Label>
            <Input
              id="imageUrl"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
            {imageUrl && (
              <div className="mt-2">
                <img
                  src={imageUrl}
                  alt="Preview"
                  className="max-h-48 rounded-lg object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content">
              Content <span className="text-destructive">*</span>
            </Label>
            <CustomEditor
              value={content}
              onChange={setContent}
              placeholder="Write your announcement content here..."
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
